import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Eye, EyeClosed, EyeClosedIcon, EyeIcon } from '@phosphor-icons/react';
import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import z from 'zod';

import { ButtonLink } from '@/components/router/ButtonLink';
import { RouterLink } from '@/components/router/RouterLink';
import type { AcceptInvitationDto } from '@/modules/api/backend';

interface InvitationFormProps {
  onSubmit: (values: AcceptInvitationDto) => Promise<void>;
}

export function InvitationForm({ onSubmit }: InvitationFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      consent: false,
    },
    onSubmit: async ({ value }) => {
      const { consent, confirmPassword, ...submitData } = value;
      await onSubmit(submitData);
    },
  });

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        await form.handleSubmit();
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h2">Anmeldung vervollständigen</Typography>

        <form.Field
          name="firstName"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Vorname ist erforderlich';

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Vorname"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              value={field.state.value}
              slotProps={{
                htmlInput: {
                  autoComplete: 'given-name',
                },
              }}
            />
          )}
        </form.Field>

        <form.Field
          name="lastName"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Nachname ist erforderlich';

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Nachname"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              value={field.state.value}
              slotProps={{
                htmlInput: {
                  autoComplete: 'family-name',
                },
              }}
            />
          )}
        </form.Field>

        <form.Field
          name="password"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Passwort ist erforderlich';
              if (value.length < 8) return 'Passwort muss mindestens 8 Zeichen lang sein';

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Passwort"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              type={showPassword ? 'text' : 'password'}
              value={field.state.value}
              slotProps={{
                htmlInput: {
                  autoComplete: 'new-password',
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? 'Verstecken' : 'Anzeigen'}
                      >
                        {showPassword ? <EyeClosed /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </form.Field>

        <form.Field
          name="confirmPassword"
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!value) return 'Passwort-Bestätigung ist erforderlich';
              const password = fieldApi.form.getFieldValue('password');
              if (value !== password) return 'Passwörter stimmen nicht überein';

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Passwort bestätigen"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              type={showPassword ? 'text' : 'password'}
              value={field.state.value}
              slotProps={{
                htmlInput: {
                  autoComplete: 'new-password',
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? 'Verstecken' : 'Anzeigen'}
                      >
                        {showPassword ? <EyeClosedIcon /> : <EyeIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </form.Field>

        <form.Field
          name="consent"
          validators={{
            onChange: z.literal(true),
          }}
        >
          {(field) => (
            <FormControl error={!!field.state.meta.errors.length}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.checked)}
                  />
                }
                label={
                  <span>
                    Hiermit akzeptiere ich die{' '}
                    <RouterLink target="_blank" to="/terms-of-use">
                      Nutzungsbedingungen
                    </RouterLink>{' '}
                    und{' '}
                    <RouterLink target="_blank" to="/privacy">
                      Datenschutzbestimmungen
                    </RouterLink>{' '}
                    des Webportals.
                  </span>
                }
              />
              {field.state.meta.errors.length > 0 && (
                <FormHelperText>{field.state.meta.errors[0]?.message}</FormHelperText>
              )}
            </FormControl>
          )}
        </form.Field>

        <form.Subscribe
          selector={(formState) => [formState.canSubmit, formState.isSubmitting, formState.values.consent]}
        >
          {([canSubmit, isSubmitting, consent]) => (
            <Stack alignItems="center" direction="row" gap={3} justifyContent="flex-end">
              <ButtonLink color="primary" to="/" variant="outlined">
                Zur Startseite
              </ButtonLink>
              <Button disabled={!canSubmit || !consent} type="submit" variant="contained">
                {isSubmitting ? <CircularProgress /> : 'Annehmen'}
              </Button>
            </Stack>
          )}
        </form.Subscribe>
      </Stack>
    </form>
  );
}
